// Week calendar arithmetic, in the device's local time (the clinic's).
import type { WorkingDay } from '@/types/api';

export const SLOT_MIN = 30;

/** Monday 00:00 of the week `date` is in. */
export function startOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const iso = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - iso);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** ISO weekday: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: Date): number {
  return ((date.getDay() + 6) % 7) + 1;
}

/** The days the calendar shows: Monday–Friday, or the whole week. */
export function weekDays(weekStart: Date, showWeekend: boolean): Date[] {
  return Array.from({ length: showWeekend ? 7 : 5 }, (_, i) => addDays(weekStart, i));
}

/** Minutes since midnight of every slot between the two hours. */
export function slotMinutes(startHour: number, endHour: number): number[] {
  const out: number[] = [];
  for (let m = startHour * 60; m < endHour * 60; m += SLOT_MIN) out.push(m);
  return out;
}

const toMinutes = (clock: string) => {
  const [h, m] = clock.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** The slot starting at `minute` lies within the opening hours of that weekday. */
export function isOpenSlot(hours: readonly WorkingDay[], weekday: number, minute: number): boolean {
  const day = hours.find((d) => d.day === weekday);
  return !!day && minute >= toMinutes(day.start) && minute < toMinutes(day.end);
}

export function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/** `day` at `minute` (local time). */
export function atMinute(day: Date, minute: number): Date {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  d.setMinutes(minute);
  return d;
}

/** Rounds to the nearest slot boundary. */
export function snap(minutes: number): number {
  return Math.round(minutes / SLOT_MIN) * SLOT_MIN;
}

export type Placed<T> = { item: T; col: number; cols: number };

/** A day's block: `preview` is a dragged item where it would land, `ghost` the same item where it is now (hidden). */
export type DayBlock<T> = Placed<T> & { key: string; preview: boolean; ghost: boolean };

/**
 * One day's blocks while an item may be dragged. `drag.item` carries the
 * dragged item's new times; `drag.here` says whether it would land on this day.
 *
 * The preview gets a key of its own and the item itself stays where it is, as a
 * hidden ghost. The element a finger holds must never unmount: touch events keep
 * going to the element first touched, and a detached one no longer bubbles them
 * to the calendar, which then cannot stop the browser from scrolling (moving a
 * booking to another day on a touch screen).
 */
export function dayBlocks<T extends { id: string; start: number; end: number }>(
  items: readonly T[],
  drag: { item: T; here: boolean } | null
): DayBlock<T>[] {
  const id = drag?.item.id;
  const shown = items.filter((i) => i.id !== id);
  if (drag?.here) shown.push(drag.item);
  const blocks = layoutDay(shown).map((p) => {
    const preview = p.item.id === id;
    return { ...p, key: preview ? `${p.item.id}:drag` : p.item.id, preview, ghost: false };
  });
  const source = items.find((i) => i.id === id);
  if (source)
    blocks.push({ item: source, col: 0, cols: 1, key: source.id, preview: false, ghost: true });
  return blocks;
}

/**
 * Side-by-side columns for bookings that overlap (several people in one
 * slot): each cluster of overlapping bookings is split into equal columns.
 */
export function layoutDay<T extends { start: number; end: number }>(
  items: readonly T[]
): Placed<T>[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  const out: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let clusterEnd = -1;
  const columnsEnd: number[] = [];
  const close = () => {
    const cols = Math.max(1, ...cluster.map((p) => p.col + 1));
    for (const p of cluster) p.cols = cols;
    out.push(...cluster);
    cluster = [];
    columnsEnd.length = 0;
  };
  for (const item of sorted) {
    if (item.start >= clusterEnd && cluster.length) close();
    let col = columnsEnd.findIndex((end) => end <= item.start);
    if (col < 0) col = columnsEnd.length;
    columnsEnd[col] = item.end;
    cluster.push({ item, col, cols: 1 });
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  if (cluster.length) close();
  return out;
}
